import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { AiResponseReviewService } from "@app/operations/ai-response-review/aiResponseReviewService";
import { AiResponseReviewQueryModel, AiResponseReviewResult } from "@app/operations/ai-response-review/interfaces";

/**
 * Root of the review list query key. Anything that edits a review should invalidate
 * this prefix so the table picks the change up.
 */
export const AI_RESPONSE_REVIEWS_QUERY_KEY = "ai-response-reviews";

interface UseAiResponseReviewsResult {
  reviews: AiResponseReviewResult[];
  totalCount: number;
  isLoading: boolean;
  isError: boolean;
  isPlaceholderData: boolean;
}

/**
 * Loads a page of AI response reviews for the given applied filters. Pass applied
 * filters only — a draft the user is still editing in the panel would fire a request
 * on every keystroke.
 */
export const useAiResponseReviews = (filters: AiResponseReviewQueryModel): UseAiResponseReviewsResult => {
  const service = useMemo(() => new AiResponseReviewService(), []);

  const { data, error, isFetching, isPlaceholderData } = useQuery({
    // Holds the previous page on screen while the next one loads, instead of
    // blanking the table on every page, sort or filter change.
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => service.getAiResponseReviews(filters, signal),
    queryKey: [AI_RESPONSE_REVIEWS_QUERY_KEY, JSON.stringify(filters)],
  });

  return {
    isError: error != null,
    isLoading: isFetching,
    reviews: data?.items ?? [],
    totalCount: data?.totalCount ?? 0,
    // While true, `data`/`totalCount` still belong to the previous filters, not the
    // ones just applied - callers that re-query with `filters` + `totalCount` (export)
    // must wait, or they'll pair the new filters with a stale row count.
    isPlaceholderData,
  };
};
